import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_TOASTER_LIFE,
  ZnMultiSelect,
  ZnButton,
  ZnSelectQueryParams,
  WizardStepComponent,
  IWizardContext,
  IWizardStepState,
  buildWizardStepDependencyMeta,
  getWizardStepData,
  persistWizardStepData,
  ZendraLocalizationServices,
} from '@zendra/shared';
import { IStepDataItem } from '../interfaces/IStepDataItem';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { HarvestPreparationCatalogs } from '../services/harvest-preparation-catalogs';
import { LocalizationPipe } from '@abp/ng.core';
import {
  IStepStandSelectionFilters,
  IStepStandSelectionOption,
  IStepStandSelectionStateMeta,
} from '../interfaces/IStepStandSelectionFilters';
import { SelectedFilter } from '../types/step-types';

@Component({
  selector: 'lib-step-stand-selection',
  standalone: true,
  imports: [ZnMultiSelect, ZnButton, LocalizationPipe],
  templateUrl: './step-stand-selection.html',
})
export class StepStandSelection implements WizardStepComponent<IStepDataItem[]> {

  private readonly service = inject(StandCycleRotationOrchestratorService);
  private readonly catalogService = inject(HarvestPreparationCatalogs);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toaster = inject(ToasterService);

  readonly propertySelect = viewChild('propertySelect', { read: ZnMultiSelect });
  readonly harvestBlockSelect = viewChild('harvestBlockSelect', { read: ZnMultiSelect });

  loading = signal(false);
  rows = signal<IStepDataItem[]>([]);
  isFiterApplied = signal(false);
  readonly localizationKeyPrefix =
    'Geographic::StandCycleRotationHarvestPreparation.StepStandSelection';
  readonly columnHeaderKeys = {
    standName: 'Planning::Entity.Stand',
    cycleNumber: 'Geographic::Entity.Cycle',
    rotationNumber: 'Geographic::Entity.Rotation',
    propertyName: 'Planning::Entity.Property',
    harvestBlockName: 'WoodSupply::Entity.HarvestBlock',
    standArea: 'Planning::Entity.Stand.StandArea',
    productivityParameterName: 'WoodSupply::Entity.ProductivityParameter',
    dab: 'Geographic::Entity.StandCycleRotation.Dab',
    stumpApplicationName: 'Geographic::Entity.StandCycleRotation.ApplyHerbicide',
    applyFeller: 'Geographic::Entity.StandCycleRotation.ApplyFeller',
    measurementTypeName: 'WoodSupply::Entity.MeasurementType',
    inventoryTypeName: 'Planning::Entity.InventoryType',
  };
  propertyOptions = signal<Array<{ key: string; value: string }>>([]);
  harvestBlockOptions = signal<Array<{ key: string; value: string }>>([]);
  selectedProperties = signal<number[]>([]);
  selectedHarvestBlocks = signal<number[]>([]);
  selectedRecords = signal<number[]>([]);
  readonly selectedRowIds = computed(() => new Set(this.selectedRecords()));
  readonly selectedRows = computed(() =>
    this.rows().filter(row => row.id !== undefined && this.selectedRowIds().has(row.id)),
  );
  readonly allRowsSelected = computed(() =>
    this.hasRows() && this.selectedRows().length === this.rows().length,
  );
  readonly someRowsSelected = computed(() =>
    this.selectedRows().length > 0 && !this.allRowsSelected(),
  );
  canClearFilters = computed(
    () => this.selectedProperties().length > 0 || this.selectedHarvestBlocks().length > 0,
  );
  canApplyFilters = computed(
    () => this.selectedProperties().length > 0 || this.selectedHarvestBlocks().length > 0,
  );
  hasRows = computed(() => this.rows().length > 0);
  isTableSectionSelected = computed(() => this.isFiterApplied() && this.hasRows());
  filterSectionClass = computed(() =>
    this.isTableSectionSelected()
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );
  tableSectionClass = computed(() => {
    if (!this.isFiterApplied()) {
      return 'zn-full-height zn-step-content-section zn-step-content-section--disabled';
    }

    if (this.isTableSectionSelected()) {
      return 'zn-full-height zn-step-content-section zn-step-content-section--selected';
    }

    return 'zn-full-height zn-step-content-section';
  });

  private temporaryId = 0;
  private loadVersion = 0;

  readonly propertyOptionsLoader = (params?: ZnSelectQueryParams) =>
    this.catalogService.loadPropertyOptions(params);

  readonly harvestBlockOptionsLoader = (params?: ZnSelectQueryParams) =>
    this.catalogService.loadHarvestBlockOptions(params);

  loadData(selectedRowIdsToRestore: number[] = this.selectedRecords()) {
    const version = ++this.loadVersion;
    const idsToRestore = new Set(selectedRowIdsToRestore);
    this.loading.set(true);
    this.selectedRecords.set([]);
    this.rows.set([]);
    this.service
      .searchHarvestPreparation({
        harvestBlockIds: this.selectedHarvestBlocks(),
        propertyIds: this.selectedProperties(),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (version === this.loadVersion) this.loading.set(false);
        }),
      )
      .subscribe({
        next: response => {
          if (version !== this.loadVersion) return;
          this.temporaryId = 0;
          const rowIds = new Set<number>();
          const rowsAux: IStepDataItem[] = (response.rows ?? []).map(item => {
            const id = this.resolveRowId(item, rowIds);
            rowIds.add(id);
            return {
              ...item,
              id,
              isInventoryValidated: false,
            };
          });

          this.rows.set(rowsAux);
          this.selectedRecords.set(
            rowsAux.flatMap(row => row.id !== undefined && idsToRestore.has(row.id) ? [row.id] : []),
          );
        },
        error: error => {
          if (version !== this.loadVersion) return;
          this.rows.set([]);
          this.selectedRecords.set([]);
          console.log(error);
        },
      });
  }

  onPropertyChange(propertiesIds: SelectedFilter) {
    this.selectedProperties.set(this.normalizeSelectedIds(propertiesIds));
  }

  onHarvestBlockChange(harvestBlocksIds: SelectedFilter) {
    this.selectedHarvestBlocks.set(this.normalizeSelectedIds(harvestBlocksIds));
  }

  onToggleRow(id: number | undefined, selected: boolean): void {
    if (this.loading() || id === undefined || !this.rows().some(row => row.id === id)) return;
    const ids = new Set(this.selectedRecords());
    if (selected) ids.add(id);
    else ids.delete(id);
    this.selectedRecords.set([...ids]);
  }

  onToggleAllRows(selected: boolean): void {
    if (this.loading()) return;
    this.selectedRecords.set(selected
      ? this.rows().flatMap(row => row.id !== undefined ? [row.id] : [])
      : []);
  }

  applyFilters() {
    this.isFiterApplied.set(true);
    this.loadData();
  }

  cleanFilters() {
    ++this.loadVersion;
    this.loading.set(false);
    this.isFiterApplied.set(false);
    this.selectedHarvestBlocks.set([]);
    this.selectedProperties.set([]);
    this.harvestBlockOptions.set([]);
    this.propertyOptions.set([]);
    this.selectedRecords.set([]);
    this.rows.set([]);
    this.propertySelect()?.reset();
    this.harvestBlockSelect()?.reset();
  }

  canLeave(context: IWizardContext): boolean {
    if (this.loading()) {
      return false;
    }

    if (this.hasRequiredSelection()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandCycleRotationHarvestPreparation.StepStandSelection.SelectionRequired`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  hasMeaningfulData(): boolean {
    return (
      this.selectedProperties().length > 0 ||
      this.selectedHarvestBlocks().length > 0 ||
      this.selectedRecords().length > 0 ||
      this.rows().length > 0 ||
      this.isFiterApplied()
    );
  }

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const state = this.getCurrentStepState(context);
    const previousData = getWizardStepData<IStepDataItem[]>(context, context.activeStepId());
    const filters = this.getStoredFilters(state);
    if (!state?.completed || !filters) {
      return;
    }

    this.selectedProperties.set(filters.propertyIds);
    this.selectedHarvestBlocks.set(filters.harvestBlockIds);
    this.propertyOptions.set(filters.propertyOptions ?? []);
    this.harvestBlockOptions.set(filters.harvestBlockOptions ?? []);
    this.syncFilterControls();
    this.isFiterApplied.set(true);
    this.loadData(this.getStoredSelectedRowIds(state, previousData));
  }

  collect(context: IWizardContext): IStepDataItem[] {
    const data = this.selectedRows();
    const meta: IStepStandSelectionStateMeta = {
      ...buildWizardStepDependencyMeta(data),
      filters: {
        propertyIds: this.selectedProperties(),
        harvestBlockIds: this.selectedHarvestBlocks(),
        propertyOptions: this.buildSelectedOptions(
          this.propertySelect(),
          this.selectedProperties(),
        ),
        harvestBlockOptions: this.buildSelectedOptions(
          this.harvestBlockSelect(),
          this.selectedHarvestBlocks(),
        ),
      },
      selectedRowIds: data
        .map(item => item.id)
        .filter((id): id is number => Number.isFinite(id)),
    };

    persistWizardStepData(context, {
      data,
      visited: true,
      dirty: true,
      completed: true,
      valid: true,
      meta,
      invalidateStepIds: [2, 3, 4],
    });
    return data;
  }

  private getTemporaryId() {
    this.temporaryId = this.temporaryId - 1;
    return this.temporaryId;
  }

  private resolveRowId(item: IStepDataItem, usedIds: Set<number>): number {
    for (const candidate of [item.id, item.standCycleRotationId]) {
      if (typeof candidate === 'number' && Number.isFinite(candidate) && !usedIds.has(candidate)) {
        return candidate;
      }
    }

    let temporaryId = this.getTemporaryId();
    while (usedIds.has(temporaryId)) {
      temporaryId = this.getTemporaryId();
    }

    return temporaryId;
  }

  private syncFilterControls(): void {
    queueMicrotask(() => {
      this.propertySelect()?.writeValue(
        this.selectedProperties().map(String)
      );
      this.harvestBlockSelect()?.writeValue(
        this.selectedHarvestBlocks().map(String)
      );
    });
  }

  private getCurrentStepState(
    context: IWizardContext,
  ): IWizardStepState<IStepDataItem[]> | undefined {
    return context.getStepState<IStepDataItem[]>(context.activeStepId());
  }

  private getStoredFilters(
    state?: IWizardStepState<IStepDataItem[]>,
  ): IStepStandSelectionFilters | null {
    const meta = state?.meta as IStepStandSelectionStateMeta | undefined;
    if (!meta?.filters) {
      return null;
    }

    return {
      propertyIds: meta.filters.propertyIds ?? [],
      harvestBlockIds: meta.filters.harvestBlockIds ?? [],
      propertyOptions: meta.filters.propertyOptions ?? [],
      harvestBlockOptions: meta.filters.harvestBlockOptions ?? [],
    };
  }

  private getStoredSelectedRowIds(
    state?: IWizardStepState<IStepDataItem[]>,
    data?: IStepDataItem[],
  ): number[] {
    const meta = state?.meta as IStepStandSelectionStateMeta | undefined;
    return meta?.selectedRowIds ?? (data ?? []).flatMap(row =>
      typeof row.id === 'number' && Number.isFinite(row.id) ? [row.id] : [],
    );
  }

  private normalizeSelectedIds(value: string | string[] | null): number[] {
    if (Array.isArray(value)) {
      return value.map(Number);
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [Number(value)];
  }

  private hasRequiredSelection(): boolean {
    return (
      (this.selectedHarvestBlocks().length > 0 || this.selectedProperties().length > 0) &&
      this.selectedRows().length > 0
    );
  }

  private buildSelectedOptions(
    select: ZnMultiSelect | undefined,
    selectedIds: number[],
  ): IStepStandSelectionOption[] {
    return selectedIds.map(id => {
      const key = String(id);

      return {
        key,
        value: select?.getLabelForId(key) ?? key,
      };
    });
  }
}
